import data from "../../shared/sharedData";

export default {
  name: "Employer Point of Contact Information",
  pageInfo: {
    text: `An employer point of contact is a person employed by the employer whose position authorizes the person to 
    provide information and supporting documentation concerning the H-2B Application for Temporary Employment Certification 
    and to communicate with the Department of Labor on behalf of the employer.  The employer point of contact should be the 
    individual most familiar with the content of the application and circumstances of the temporary employment offered through 
    this application.<br></br><b><u>Important Note</b></u>:  The employer point of contact information in this section, specifically 
    the name, telephone number, and email address, <u>must be different</u> from the attorney/agent information listed in Section E, 
    unless the attorney or agent is an employee of the employer.
    `,
    coloring: "grey",
  },
  sections: [
    {
      name: "Employer Point of Contact Name & Title",
      fields: [
        {
          widget: "inlinePrefiller",
          prefillLabel:
            "Select an Employer Point of Contact profile to populate this section (optional)",
          code: "D",
          prefill: {
            schema: "employerPocs",
            type: "employer-pocs",
            formName: "9142B",
            formSection: "D-1",
          },
        },
        {
          widget: "inlinePrefiller",
          prefillLabel:
            "Select an Attorney/Agent profile to populate this section if the attorney is an employee of the employer (optional)",
          code: "D",
          prefill: {
            schema: "agentAttorney",
            type: "agent-attorney-individs",
            formName: "9142B",
            formSection: "D-2",
          },
        },
        {
          code: "D.1",
          machine_name: "emppocLastname",
          widget: "text",
          label: "Contact's Last (family) Name",
          validate: [
            "required",
            // {
            //   minLength: 2,
            //   message: "Contact's full Last Name must contain at least 2 characters.",
            // },
          ],
          maxLength: 35,
          tooltip:
            "Enter the last (family) name of the employer’s point of contact.",
        },
        {
          code: "D.2",
          machine_name: "emppocFirstname",
          widget: "text",
          label: "First (given) Name",
          validate: [
            "required",
            // {
            //   minLength: 2,
            //   message: "Contact's full First Name must contain at least 2 characters.",
            // },
          ],
          maxLength: 35,
          tooltip:
            "Enter the first (given) name of the employer’s point of contact.",
        },
        {
          code: "D.3",
          machine_name: "emppocMiddlename",
          widget: "text",
          label: "Middle name(s)",
          maxLength: 35,
          tooltip:
            "Enter the middle name of the employer’s point of contact, if applicable. Enter “N/A” if not applicable.",
        },
        {
          code: "D.4",
          machine_name: "emppocJobtitle",
          widget: "text",
          label: "Contact's Job Title",
          validate: [
            "required",
            // {
            //   minLength: 5,
            //   message: "Contact's full Job Title must contain at least 5 characters.",
            // },
          ],
          maxLength: 60,
          tooltip: "Enter the job title of the employer's point of contact.",
        },
      ],
    },
    {
      name: "Employer Point of Contact Address",
      fields: [
        {
          code: "D.5",
          machine_name: "emppocAddr1",
          widget: "text",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full street address.",
            },
          ],
          maxLength: 60,
          tooltip:
            "Enter the business street address for the employer’s point of contact. The address must be a physical location and not a P.O. Box.",
        },
        {
          code: "D.6",
          machine_name: "emppocAddr2",
          widget: "text",
          label:
            "<span>Address 2 <i>(apartment/suite/floor and number)</i></span>",
          maxLength: 60,
          tooltip:
            "If additional space is needed for the street address, use this field to complete the street address. If no additional space is needed, enter “N/A.”",
        },
        {
          code: "D.10",
          machine_name: "emppocCountry",
          widget: "select",
          label: "Country",
          selection: data.country,
          clearSchema: ["emppocState", "emppocProvince"],
          validate: ["required"],
          tooltip: "Enter the country of the employer’s point of contact.",
        },
        {
          code: "D.7",
          machine_name: "emppocCity",
          widget: "text",
          label: "City",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full City.",
            },
          ],
          maxLength: 35,
          tooltip: "Enter the city of the employer’s point of contact.",
        },
        {
          code: "D.8",
          machine_name: "emppocState",
          widget: "select",
          label: "State",
          selection: data.state,
          state: true,
          isVisible: {
            when: "emppocCountry",
            is: "UNITED STATES OF AMERICA",
          },
          validate: ["required"],
          tooltip:
            "Enter the State, District, or Territory of the employer's point of contact.",
        },
        {
          code: "D.9",
          machine_name: "emppocPostcode",
          widget: "text",
          label: "Postal Code",
          validate: [
            {
              postalCountry: "emppocCountry",
              needsFieldValues: true,
              extraData: {
                valueOf: "emppocCountry",
              },
            },
            {
              isRequired: "emppocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          maxLength: 10,
          tooltip:
            "Enter the postal (zip) code of the employer's point of contact.",
        },
        {
          code: "D.11",
          machine_name: "emppocProvince",
          widget: "text",
          label: "Province",
          maxLength: 25,
          validate: [
            {
              isRequired: "emppocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
            {
              minLengthWithNA: 5,
              message: "Province must contain at least 5 characters.",
            },
          ],
          tooltip:
            "Enter the province of the employer's point of contact, if applicable. Enter “N/A” if not applicable.",
        },
      ],
    },
    {
      name: "Employer Point of Contact - Contact Information",
      fields: [
        {
          code: "D.12",
          machine_name: "emppocPhone",
          widget: "phone",
          label: "Telephone Number",
          validate: ["required", "phone"],
          tooltip:
            "Enter the area code and business telephone number of the employer's point of contact.  Include country code, if the point of contact is located outside the United States.",
        },
        {
          code: "D.13",
          machine_name: "emppocPhoneext",
          widget: "text",
          label: "Extension",
          validate: ["number"],
          parse: "positiveWholeNumber",
          maxLength: 4,
          tooltip:
            "Enter the extension of the telephone number of the employer's point of contact, if applicable. Enter “N/A” if not applicable.",
        },
        {
          code: "D.14",
          machine_name: "emppocEmail",
          widget: "text",
          label: "Business e-mail address",
          validate: [
            "required",
            {
              email: {
                extraData: {
                  allowNAs: true,
                },
              },
            },
            {
              maxLength: 55,
              message: "This field cannot be more than 55 characters long.",
            },
          ],
          maxLength: 55,
          tooltip: `Enter the business email address of the employer's point of contact in the format name@emailaddress.top-leveldomain. 
            The email entered in this field must be the same as the one regularly used by the employer’s point of contact for 
            its business operations and capable of sending and receiving electronic communications from the Department with 
            respect to the processing of this application. If the employer’s point of contact does not possess a business 
            email address, please enter “N/A.”`,
        },
      ],
    },
  ],
};
